import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { LocaleList, LocaleFilters } from '../../components/locali'
import { MapContainer } from '../../components/map'
import { Skeleton } from '../../components/common'
import { FiltriLocali, TipoLocale, Provincia, FasciaPrezzo } from '../../types'
import { useLocaliStore, useFavoritiStore } from '../../store'

/**
 * Pagina elenco locali con filtri e mappa
 */
export default function Locali() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Store per locali e favoriti
  const { locali, loading, fetchLocali } = useLocaliStore()
  const { favoritiIds, loadFavoriti, toggleFavorito } = useFavoritiStore()

  // Stato vista (lista o mappa)
  const [vistaAttiva, setVistaAttiva] = useState<'lista' | 'mappa'>('lista')

  // Inizializza filtri da URL params
  const filtri: FiltriLocali = {
    provincia: searchParams.get('provincia') as Provincia | undefined,
    tipo: searchParams.get('tipo') as TipoLocale | undefined,
    fasciaPrezzo: searchParams.get('prezzo') as FasciaPrezzo | undefined,
    ricerca: searchParams.get('q') || undefined,
  }

  // Carica locali e favoriti all'avvio e quando cambiano i filtri
  useEffect(() => {
    fetchLocali(filtri)
    loadFavoriti()
  }, [searchParams])

  // Aggiorna filtri e URL
  const handleFiltriChange = (nuoviFiltri: FiltriLocali) => {
    const params = new URLSearchParams()
    if (nuoviFiltri.provincia) params.set('provincia', nuoviFiltri.provincia)
    if (nuoviFiltri.tipo) params.set('tipo', nuoviFiltri.tipo)
    if (nuoviFiltri.fasciaPrezzo) params.set('prezzo', nuoviFiltri.fasciaPrezzo)
    if (nuoviFiltri.ricerca) params.set('q', nuoviFiltri.ricerca)
    setSearchParams(params)
  }

  // Toggle favorito
  const handleFavoritoClick = (localeId: number) => {
    toggleFavorito(localeId)
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-4xl md:text-5xl text-white mb-2">
          I Locali <span className="text-gold">Approved</span>
        </h1>
        <p className="text-gray-400">
          Tutti i ristoranti, trattorie e agriturismi approvati dal club
        </p>
      </div>

      {/* Filtri */}
      <div className="mb-8">
        <LocaleFilters
          filtri={filtri}
          onFiltriChange={handleFiltriChange}
          totaleRisultati={locali.length}
        />
      </div>

      {/* Toggle vista */}
      <div className="flex justify-end mb-6">
        <div className="bg-night-light rounded-lg p-1 inline-flex">
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              vistaAttiva === 'lista'
                ? 'bg-gold text-night'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setVistaAttiva('lista')}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              Lista
            </span>
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              vistaAttiva === 'mappa'
                ? 'bg-gold text-night'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setVistaAttiva('mappa')}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              Mappa
            </span>
          </button>
        </div>
      </div>

      {/* Contenuto */}
      {loading ? (
        vistaAttiva === 'lista' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-80 rounded-xl" />
            ))}
          </div>
        ) : (
          <Skeleton className="h-[calc(100vh-400px)] min-h-[400px] rounded-xl" />
        )
      ) : vistaAttiva === 'lista' ? (
        <LocaleList
          locali={locali}
          favoriti={favoritiIds}
          onFavoritoClick={handleFavoritoClick}
        />
      ) : (
        <MapContainer
          locali={locali}
          height="calc(100vh - 400px)"
          className="min-h-[400px]"
        />
      )}
    </div>
  )
}
